/**
 * How many answerable parts a question has, and how many of them the child has filled in.
 *
 * WHY THIS EXISTS. The submit button used to unlock on the FIRST answer, so a two-part matching
 * question could be handed in with one pair placed - which the PM photographed. The marker treats
 * an unfilled part as wrong, so that child lost a mark they could have had. Submit now waits for
 * every part.
 *
 * ⚠️ THIS REVERSES A DELIBERATE DECISION, on the PM's instruction (2026-10-26). The old rule was
 * "any part", reasoned as: a child who can do two of three blanks should be able to hand that in,
 * and demanding all three pushes them to guess or to skip a question they partly knew. That cost
 * is real and has not gone away - it is now paid deliberately, and "Skip for now" is still the
 * honest way out of a question they cannot finish. The player shows how many parts are left so
 * the grey button is never a mystery.
 *
 * ⚠️ THE PART LIST MIRRORS gradeTargets IN tutor-backend's answerGrading.js, which is the
 * authority on what a question's targets are. If the two disagree, this one blocks a child from
 * submitting something the server would happily mark. Change them together.
 */

import type { EngDiagStudentAnswers } from "./types/eng-diag.types"
import { WORKED_TYPE_BASE } from "./components/EngDiagQuestionView"

// The read-aloud types, from the runner - one copy, and it already states that it mirrors
// NON_GRADED_TYPES on the server. Re-exported so this module's own callers can reach it.
export { NOTHING_TO_ANSWER } from "./hooks/useEngAttemptRunner"
import { NOTHING_TO_ANSWER as NOTHING } from "./hooks/useEngAttemptRunner"

/**
 * The answer keys a question is answered under, or null when the count cannot be known from
 * what the student was sent.
 *
 * Null is not "no parts" - it means "one thing, and we cannot say how complete it is". Two types
 * are like that, and both are answered under a single key:
 *
 *   text_select      how many words to pick is the answer, so requiring a count would give it
 *                    away.
 *   sentence_rebuild the word pool includes distractors, and the server deliberately merges them
 *                    into `words`, so "every word placed" is not a thing the client can compute -
 *                    and would be wrong anyway.
 *
 * For those, any answer at all is enough, which is what the old rule did for everything.
 */
export function answerablePartIds(question: {
  id?: string
  type?: string
  options?: any
}): string[] | null {
  if (!question || !question.type) return []
  const type = WORKED_TYPE_BASE[question.type] ?? question.type
  if (NOTHING.includes(type)) return []

  const options = question.options || {}
  const ids = (list: any[] | undefined) =>
    (list || []).map((item: any) => String(item?.id ?? "")).filter(Boolean)

  switch (type) {
    // Answered under the question's own id, not a part id.
    case "mcq":
      return question.id ? [String(question.id)] : []

    case "blank":
    case "mc_cloze":
    case "spelling_drop":
    case "punctuation_drop":
      return ids(options.blanks)

    case "open_ended":
      return ids(options.fields)

    case "column_match":
      return ids(options.pairs)

    case "image_sort":
      return ids(options.items)

    case "punctuation_insert":
      return ids(options.sites)

    case "reading_comp":
      return ids(options.subQuestions)

    case "text_select":
    case "sentence_rebuild":
      return null

    default:
      // An unknown type must not become unanswerable. Falls back to the old behaviour.
      return null
  }
}

export type AnswerProgress = {
  /** Parts with something in them. */
  answered: number
  /** Parts in total, or 0 for a question with nothing to answer. */
  total: number
  /** Whether the child may hand it in. */
  complete: boolean
}

const filled = (value: unknown) => String(value ?? "").trim() !== ""

/** How much of this question is done. */
export function answerProgress(
  question: { id?: string; type?: string; options?: any } | null | undefined,
  answers: EngDiagStudentAnswers,
): AnswerProgress {
  const given = answers || {}
  const parts = answerablePartIds(question || {})

  // Unknowable count: one answer is enough, as it always was.
  if (parts === null) {
    const any = Object.values(given).some(filled)
    return { answered: any ? 1 : 0, total: 1, complete: any }
  }
  // Nothing to answer - the read-aloud types. Submittable untouched.
  if (parts.length === 0) return { answered: 0, total: 0, complete: true }

  const answered = parts.filter(id => filled(given[id])).length
  return { answered, total: parts.length, complete: answered === parts.length }
}
